/**
 * welcome.ts — first-mount banner + interactive 5-step tutorial.
 *
 * Phase CG. The welcome banner is the developer's first impression — it
 * has to teach the framework in 6 lines. The tutorial then walks them
 * through running a real operator, viewing state, spawning an agent.
 */

import { ZeqondClock } from "../orb/zeqond-clock.js";
import { esc } from "./formatters.js";
import { Api, getIdentity } from "./api-client.js";

/** First-mount welcome banner. Returns a DOM element to append. */
export function renderWelcome(): HTMLElement {
  const id = getIdentity();
  const tick = ZeqondClock.now();
  const machine = id.slug ?? "(off-machine)";
  const tier = id.tier ?? "guest";
  const zid = id.zid ?? "—";

  const el = document.createElement("div");
  el.className = "cli-welcome";
  el.innerHTML =
    '<div class="cli-welcome-head">' +
      '<span class="cli-welcome-bar"></span>' +
      '<span class="cli-welcome-title">Zeq Root — the CLI · ZeqVM terminal</span>' +
      '<span class="cli-welcome-pulse" title="HulyaPulse 1.287 Hz"></span>' +
    '</div>' +
    '<div class="cli-welcome-line">' +
      'Zeq is <strong>ZeqVM</strong> — a mathematical physics virtual machine. Use ours, or fork your own in seconds.' +
    '</div>' +
    '<div class="cli-welcome-grid">' +
      '<div><span class="k">clock</span><span class="v">HulyaPulse 1.287 Hz · τ = 0.777 s · 1 Zeqond</span></div>' +
      '<div><span class="k">tolerance</span><span class="v">≤0.1% (KO42 imperative)</span></div>' +
      '<div><span class="k">operators</span><span class="v">1,500+ catalogued · 42+ Kinematic surface</span></div>' +
      '<div><span class="k">machine</span><span class="v">' + esc(machine) + ' · tier ' + esc(tier) + '</span></div>' +
      '<div><span class="k">ZID</span><span class="v"><code>' + esc(zid) + '</code></span></div>' +
      '<div><span class="k">Zeqond now</span><span class="v"><code>' + tick.zeqond + '</code> · phase ' + tick.phase.toFixed(4) + '</span></div>' +
    '</div>' +
    '<div class="cli-welcome-cta">' +
      '→ type <code>tutorial</code> to start · <code>help</code> for the full surface · or try <code>hello universe</code>' +
    '</div>';
  return el;
}

// ── Interactive tutorial ────────────────────────────────────────────────
// Row 12.b (2026-06-12): rebuilt from a 5-card slideshow into a 7-step
// ACTION-DRIVEN onboarding — each step is a real thing the user does, and
// the lifecycle steps probe live state (signed in? which machine?) so the
// tutorial meets the user where they actually are. Progress persists in
// localStorage ("zeq.cli.tutorial.step"), which in the native CLI is
// file-backed (~/.zeqrc) — one-shot `zeq tutorial` invocations resume.
// Environment-aware: native terminal vs workbench get the right wording.

export interface TutorialState {
  step: number;
  active: boolean;
}

export const TUTORIAL_STEPS = 7;

function native(): boolean {
  return !!(globalThis as unknown as { __ZEQ_NATIVE__?: boolean }).__ZEQ_NATIVE__;
}

/** Render one tutorial step. Step is 1-indexed. */
export async function renderTutorialStep(step: number): Promise<HTMLElement> {
  const el = document.createElement("div");
  el.className = "cli-tutorial-step";
  const isN = native();

  const head =
    '<div class="cli-tut-head">' +
      '<span class="cli-tut-num">step ' + step + ' / ' + TUTORIAL_STEPS + '</span>' +
      '<span class="cli-tut-title">' + esc(TUT_TITLES[step - 1] ?? "") + '</span>' +
    '</div>';

  let body = "";
  let live = "";

  switch (step) {
    case 1: {
      body =
        '<p>The framework runs on a fixed system clock — <strong>HulyaPulse, 1.287 Hz</strong>. ' +
        'One tick is a <strong>Zeqond</strong>: 1 Z = 0.777 s — definitional, like the SI second (τ = 777,000,777 ns exactly).</p>' +
        '<p>Every compute, every transition, every audit row is stamped with the Zeqond it happened in. ' +
        'Durations here are <code>Z</code> (Zeqonds) or <code>mz</code> (millizeqonds, 1 mz = 0.777 ms) — never seconds.</p>' +
        '<p>See it tick:</p>' +
        '<pre><code>pulse</code></pre>';
      const tick = ZeqondClock.now();
      live =
        '<div class="cli-tut-live">' +
          '<strong>Right now:</strong> Zeqond <code>' + tick.zeqond + '</code> · phase <code>' + tick.phase.toFixed(6) + '</code>' +
        '</div>';
      break;
    }
    case 2: {
      const id = getIdentity();
      const signedIn = !!(id.zid || id.slug);
      body =
        '<p>Your account here is not a password — it\'s an <strong>equation</strong>. ' +
        'One command creates everything: the 7-step wizard derives an account equation from your phrase, ' +
        'registers its HMAC hash (the server <em>never</em> sees the raw equation), and mints your <strong>ZID</strong>, ' +
        'your own <strong>state machine</strong>, and an <strong>API key</strong>' +
        (isN ? ' — saved as your active profile' : '') + ':</p>' +
        '<pre><code>signup "kepler orbits traced in winter starlight"</code></pre>' +
        '<p>Use your own phrase — at least 4 meaningful words. ' +
        '<strong>The issued equation is the credential</strong> (shown once, unrecoverable); the phrase only seeds it at this exact Zeqond.</p>' +
        '<p>Already have an identity? <code>signin &lt;ZID&gt;</code>' +
        (isN ? ' — you\'ll be prompted for the equation (input hidden).' : ' "&lt;equation&gt;"') + '</p>';
      live = signedIn
        ? '<div class="cli-tut-live"><strong>✓ already done</strong> — signed in as <code>' + esc(id.zid ?? "—") + '</code>' +
          (id.slug ? ' · machine <code>' + esc(id.slug) + '</code>' : '') + ' — type <code>tutorial next</code></div>'
        : '<div class="cli-tut-live">status: <strong>not signed in</strong> — run <code>signup</code> above, then <code>tutorial next</code></div>';
      break;
    }
    case 3: {
      body =
        '<p>The kernel catalogues <strong>1,500+ operators</strong> — closed-form physics and math procedures (QM, NM, GR, CS, …). ' +
        'Run Newton\'s 2nd law:</p>' +
        '<pre><code>compute NM19 mass=5 acceleration=2</code></pre>' +
        '<p>What comes back is a <strong>CKO envelope</strong>, not a bare number: value + units, the KO42 carrier, ' +
        'the Zeqond + phase it ran in, the ≤0.1% precision deviation, the registry version, and an <strong>Ed25519 signature</strong> from this node.</p>' +
        '<p>Browse the catalogue with <code>operators</code> · <code>operators GR</code>.</p>';
      break;
    }
    case 4: {
      body =
        '<p>Every envelope is a verifiable receipt. Compute the Schwarzschild radius of the Sun, save it, verify it:</p>' +
        (isN
          ? '<pre><code>zeq compute GR37 mass=1.98892e30 --json &gt; sun.json\nzeq verify sun.json</code></pre>'
          : '<pre><code>compute GR37 mass=1.98892e30 --json</code></pre>' +
            '<p>…copy the JSON, then run <code>verify &lt;paste the JSON&gt;</code>.</p>') +
        '<p><code>verify</code> runs both trust legs: the <strong>Ed25519 signature</strong> against the origin\'s published node key, ' +
        'and an <strong>independent recompute</strong> through the verifier mesh. ' +
        'If either disagrees, it tells you. No trust required — that\'s the point.</p>';
      break;
    }
    case 5: {
      body =
        '<p>Your computes from steps 3–4 already landed as rows in your machine\'s <strong>entangled state</strong> — ' +
        'every row\'s hash depends on the previous row\'s hash, so altering any row breaks every hash after it.</p>' +
        '<pre><code>state head     # top of the entangled state\nstate audit    # recent rows — find your computes\nstate explore  # full explorer</code></pre>';
      break;
    }
    case 6: {
      body =
        '<p>Now wire your code to the SDK. Mint a headless API key (the Bearer your app uses — raw key shown once):</p>' +
        '<pre><code>keys mint my-app write</code></pre>' +
        '<p>Then get a quickstart wired to <em>your</em> key and origin — REST curl, JavaScript, the MCP server for AI clients, and the kernel skill:</p>' +
        '<pre><code>sdk</code></pre>';
      break;
    }
    case 7: {
      body =
        '<p>You have an identity, a machine, a verified compute, and an SDK key. Where to go from here:</p>' +
        '<ul>' +
          '<li><code>agents spawn "find the lowest-energy orbit for a 1kg sat at 400km"</code> — LLM agents that tick on the HulyaPulse and write reasoning into your entangled state</li>' +
          '<li><code>contract</code> — the state-machine contract surface</li>' +
          '<li><code>site</code> — your machine\'s hosted pages</li>' +
          (isN
            ? '<li><code>zeq --script build.zeq</code> — script files · <code>echo "pulse" | zeq</code> — pipes · <code>zeq update</code> — self-update from your origin</li>'
            : '<li>the <strong>Queries</strong> tab — visual contract IDE · the <strong>Pulse</strong> tab — BYOK chat with framework awareness</li>') +
          '<li><code>examples</code> — 20+ copy-paste recipes · <code>help</code> — the full surface · <code>docs &lt;topic&gt;</code> — SDK docs</li>' +
        '</ul>' +
        '<p>Every numeric claim the CLI makes is sourced to the kernel constants — ≤0.1% applies to every byte. Go build.</p>';
      break;
    }
  }

  const foot = step < TUTORIAL_STEPS
    ? '<div class="cli-tut-foot">→ <code>tutorial next</code> to advance · <code>tutorial back</code> · <code>tutorial exit</code> · progress is saved</div>'
    : '<div class="cli-tut-foot">→ tutorial complete · <code>tutorial reset</code> to run it again · <code>help</code> to explore</div>';

  el.innerHTML = head + '<div class="cli-tut-body">' + body + live + '</div>' + foot;
  return el;
}

const TUT_TITLES = [
  "The clock — Zeqonds",
  "Mint your identity + machine",
  "Run your first operator",
  "Verify the receipt — the trust loop",
  "The entangled state",
  "Link the SDK — keys + quickstart",
  "Go build",
];

/** Soft probe — surface api-core reachability + signed-in identity. */
export async function welcomeProbe(): Promise<{ healthy: boolean; signedIn: boolean }> {
  const id = getIdentity();
  const r = await Api.health();
  return { healthy: r.ok, signedIn: !!(id.slug || id.zid) };
}
